import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Link,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import BarChartIcon from "@mui/icons-material/BarChart";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import MonitorOutlinedIcon from "@mui/icons-material/MonitorOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Drawer } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../../Hooks/useAuth";

import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Menu, MenuItem } from "@mui/material";
import { Helmet } from "react-helmet-async";
import {
  loginUser,
  getUserDetails,
  forgotPassword,
} from "../../Services/authService";
import { ROUTES, STORAGE_KEYS } from "../../Utils/constants";
import logo from "../../assets/logo.svg";

import "./PricingFinance/pricingfinance.css";
import LoadingAnimation from "../../Components/Common/LoadingAnimation";

const PURPLE = "#5B45FF";
const PURPLE_DARK = "#4A38E0";

function FeatureCard({ icon, title, desc }) {
  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "#fff",
        border: "1px solid #f1f1f4",
        borderRadius: "16px",
        p: 4,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        transition: "box-shadow .2s ease",
        "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.06)" },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "12px",
          bgcolor: "#F7F7F9",
          color: "#334155",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{ fontWeight: 700, fontSize: "18px", color: "#0f172a", mb: 1 }}
      >
        {title}
      </Typography>
      <Typography sx={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>
        {desc}
      </Typography>
    </Box>
  );
}

function SpecBlock({ title, items }) {
  return (
    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "18px",
          color: "#0f172a",
          pb: 1.5,
          mb: 2,
          borderBottom: "1px solid #f1f1f4",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {items.map((text) => (
          <Box
            key={text}
            sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
          >
            <CheckCircleIcon
              sx={{ fontSize: 18, color: PURPLE, opacity: 0.85, mt: "2px" }}
            />
            <Typography
              sx={{ color: "#475569", fontSize: "14px", lineHeight: 1.5 }}
            >
              {text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function DarkPricingCard({
  name,
  price,
  period,
  desc,
  features,
  getStartedLabel,
}) {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        borderRadius: "24px",
        p: 4,
        border: "1px solid #2c2c2c",
      }}
    >
      <Typography
        sx={{ fontWeight: 700, fontSize: "20px", color: "#0f172a", mb: 1 }}
      >
        {name}
      </Typography>
      <Typography
        sx={{ color: "#64748b", fontSize: "13px", minHeight: 40, mb: 3 }}
      >
        {desc}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 3 }}>
        <Typography
          sx={{ fontWeight: 800, fontSize: "40px", color: "#0f172a" }}
        >
          {price}
        </Typography>
        {period && (
          <Typography sx={{ color: "#64748b", fontWeight: 500 }}>
            {period}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          flex: 1,
          mb: 3,
        }}
      >
        {features.map((f) => (
          <Box
            key={f}
            sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 18,
                mt: "2px",
                color: PURPLE,
              }}
            />
            <Typography sx={{ fontSize: "13px", color: "#475569" }}>
              {f}
            </Typography>
          </Box>
        ))}
      </Box>
      <Button
        sx={{
          color: "#fff",
          background: "#2c2c2c",
          borderRadius: "50px",
          width: "100%",
          height: "60px",
        }}
        onClick={() => {
          window.location.href = "https://vendinaf.com/en/nafcloud#pricing";
        }}
      >
        {getStartedLabel}
      </Button>
    </Box>
  );
}

const NAFLandingLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const { t, i18n } = useTranslation();

  const [langAnchorEl, setLangAnchorEl] = useState(null);

  // NOTE: code must match your i18next resource keys (e.g. locales/zh.json -> "zh")
  const languages = [
    { code: "de", label: "Deutsch" },
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
    { code: "it", label: "Italiano" },
    { code: "ch", label: "Chinese" },
  ];

  const openLangMenu = Boolean(langAnchorEl);

  const handleLangMenuOpen = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    handleLangMenuClose();
  };

  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash) return;
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        setTimeout(scrollToHash, 300);
      }
    };
    scrollToHash();
  }, []);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ identifier: "", password: "" });
  const [errors, setErrors] = useState({});
  const [checked, setChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [forgotDialogOpen, setForgotDialogOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotEmailError, setForgotEmailError] = useState("");

  //const[loading,setLoding] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateIdentifier = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10,15}$/;
    const usernameRegex = /^.{3,30}$/;
    if (emailRegex.test(value)) return true;
    if (phoneRegex.test(value)) return true;
    return usernameRegex.test(value);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.identifier) {
      newErrors.identifier = t("login.errors.identifierRequired");
    } else if (!validateIdentifier(formData.identifier)) {
      newErrors.identifier = t("login.errors.identifierInvalid");
    }

    if (!formData.password) {
      newErrors.password = t("login.errors.passwordRequired");
    } else if (formData.password.length < 3) {
      newErrors.password = t("login.errors.passwordTooShort");
    }

    if (!checked) {
      newErrors.checkbox = t("login.errors.checkboxRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const { accessToken } = await loginUser(formData);

      const data = await getUserDetails();

      const roles = data?.authorities;
      const franchiseId = data?.franchiseId;

      login(accessToken, {
        roles,
        franchiseId,
      });

      navigate(ROUTES.HOME);
    } catch (error) {
      let errorMessage = t("login.errors.somethingWentWrong");

      switch (error.response?.status) {
        case 500:
          errorMessage = t("login.errors.serverError");
          break;

        default:
          errorMessage = t("login.errors.invalidCredentials");
      }

      setErrors((prev) => ({
        ...prev,
        form: errorMessage,
      }));
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleForgotPassword = async () => {
    if (!formData.identifier) {
      setErrors((prev) => ({
        ...prev,
        identifier: t("login.errors.identifierRequiredReset"),
      }));
      return;
    }
    try {
      await forgotPassword(formData.identifier);
      setSnackbarMessage(t("login.snackbar.resetSuccess"));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(t("login.snackbar.resetError"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const textFieldSx = {
    bgcolor: "#F9FAFB",
    borderRadius: "12px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      "& fieldset": { borderColor: "#e5e7eb" },
      "&:hover fieldset": { borderColor: "#d1d5db" },
      "&.Mui-focused fieldset": { borderColor: PURPLE, borderWidth: "1.5px" },
    },
    "& .MuiInputBase-input": { color: "#0f172a" },
    "& .MuiInputBase-input::placeholder": { color: "#94a3b8", opacity: 1 },
  };

  // Arrays / nested objects pulled from i18n with returnObjects: true
  const specSalesItems = t("specifications.salesPerformance.items", {
    returnObjects: true,
  });
  const specMachineItems = t("specifications.machines.items", {
    returnObjects: true,
  });
  const specProductItems = t("specifications.products.items", {
    returnObjects: true,
  });
  const specUserSecurityItems = t("specifications.userSecurity.items", {
    returnObjects: true,
  });

  const basicFeatures = t("pricing.basic.features", { returnObjects: true });
  const premiumFeatures = t("pricing.premium.features", {
    returnObjects: true,
  });
  const enterpriseFeatures = t("pricing.enterprise.features", {
    returnObjects: true,
  });
  

  const [pageLoading, setPageLoading] = useState(true);
  useEffect(() => {
    setTimeout(() => {
      setPageLoading(false);
    }, 500);
  }, []);

  if (pageLoading) {
    return <LoadingAnimation />;
  }

  return (
    <>
      <Helmet>
        <html lang="de" />

        <title>
          NAF Cloud System | Cloud-Software für intelligente Verkaufsautomaten
        </title>

        <meta
          name="description"
          content="NAF Cloud System ist eine cloudbasierte Software zur Verwaltung von Verkaufsautomaten. Überwachen Sie Umsätze, Lagerbestände, Maschinenstatus, Werbung und Benutzer in Echtzeit."
        />

        <meta
          name="keywords"
          content="Verkaufsautomaten Software, Automatenverwaltung, Cloud Software, Verkaufsautomaten Management, Lagerverwaltung, Verkaufsanalyse, Fernüberwachung, Nayax Integration, Smart Vending, Automaten Cloud, CRM, Steuerberichte"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="NAF Cloud System" />

        <link rel="canonical" href="https://naf-cloudsystem.de/" />

        <meta
          property="og:title"
          content="NAF Cloud System | Cloud-Software für Verkaufsautomaten"
        />

        <meta
          property="og:description"
          content="Verwalten Sie Ihre Verkaufsautomaten effizient mit Echtzeit-Analysen, Lagerverwaltung und Fernüberwachung."
        />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:url" content="https://naf-cloudsystem.de/" />
        <meta
          property="og:image"
          content="https://naf-cloudsystem.de/logo.png"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="NAF Cloud System | Verkaufsautomaten Software"
        />
        <meta
          name="twitter:description"
          content="Cloudbasierte Software für Verkaufsautomaten mit Verkaufsanalysen, Lagerverwaltung und Fernüberwachung."
        />
      </Helmet>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#fff",
          color: "#0f172a",
          fontFamily: "Satoshi, sans-serif",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 50,
            bgcolor: "rgba(255,255,255,0.8)",
            backdropFilter: "blur(8px)",
            borderBottom: "1px solid #f1f1f4",
          }}
        >
          <Box
            sx={{
              maxWidth: "1280px",
              mx: "auto",
              px: { xs: 2, sm: 3, lg: 4 },
              height: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                component="img"
                src={logo}
                alt="Logo"
                sx={{ height: 44, width: "auto" }}
              />

              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "20px",
                  color: "#1e293b",
                }}
              >
                Cloud System
              </Typography>
            </Box>

            {/* Desktop menu */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: 4,
                fontSize: "14px",
                color: "#64748b",
                fontWeight: 500,
                alignItems: "center",
              }}
            >
              <Link href="#features" underline="none" color="inherit">
                {t("nav.features")}
              </Link>

              <Link href="#specifications" underline="none" color="inherit">
                {t("nav.specifications")}
              </Link>

              {/* <Link href="#pricing" underline="none" color="inherit">
                {t("nav.pricing")}
              </Link> */}

              <Box
                onClick={handleLangMenuOpen}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  px: 2,
                  py: 1,
                  borderRadius: "30px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <Typography sx={{ fontSize: 14 }}>
                  {languages.find((lang) => lang.code === i18n.language)
                    ?.label || t("nav.language")}
                </Typography>

                <KeyboardArrowDownIcon
                  sx={{
                    ml: 0.5,
                    transform: openLangMenu ? "rotate(180deg)" : "rotate(0deg)",
                    transition: ".3s",
                  }}
                />
              </Box>
            </Box>

            {/* Mobile hamburger */}
            <IconButton
              sx={{
                display: { xs: "flex", md: "none" },
                color: "#1e293b",
              }}
              onClick={() => setMobileMenuOpen(true)}
            >
              <MenuIcon fontSize="large" />
            </IconButton>
          </Box>

          {/* Language menu */}
          <Menu
            anchorEl={langAnchorEl}
            open={openLangMenu}
            onClose={handleLangMenuClose}
          >
            {languages.map(({ code, label }) => (
              <MenuItem
                key={code}
                selected={i18n.language === code}
                onClick={() => handleLanguageChange(code)}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          PaperProps={{
            sx: {
              width: 260,
              bgcolor: "#fff",
              color: "#000",
              height: "100%",
            },
          }}
        >
          <Box
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 3,
              height: "100%",
            }}
          >
            {/* Close icon */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Language dropdown */}
              <Box
                onClick={handleLangMenuOpen}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "160px",
                  px: 2,
                  py: 1,
                  border: "1px solid #e5e7eb",
                  borderRadius: "30px",
                  cursor: "pointer",
                  bgcolor: "#000",
                  color: "#fff",
                }}
              >
                <Typography sx={{ fontSize: 14 }}>
                  {languages.find((lang) => lang.code === i18n.language)
                    ?.label || t("nav.language")}
                </Typography>

                <KeyboardArrowDownIcon />
              </Box>

              <IconButton
                onClick={() => setMobileMenuOpen(false)}
                sx={{ color: "#000" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Link
              href="#features"
              underline="none"
              onClick={() => setMobileMenuOpen(false)}
              sx={{ color: "#000" }}
            >
              {t("nav.features")}
            </Link>

            <Link
              href="#specifications"
              underline="none"
              onClick={() => setMobileMenuOpen(false)}
              sx={{ color: "#000" }}
            >
              {t("nav.specifications")}
            </Link>

            <Link
              href="#pricing"
              underline="none"
              onClick={() => setMobileMenuOpen(false)}
              sx={{ color: "#000" }}
            >
              {t("nav.pricing")}
            </Link>
          </Box>
        </Drawer>
        {/* Hero + Login */}
        <Box sx={{ position: "relative", pt: 10, pb: 16, overflow: "hidden" }}>
          <Box
            sx={{
              maxWidth: "1280px",
              mx: "auto",
              px: { xs: 2, sm: 3, lg: 4 },
              position: "relative",
              zIndex: 1,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: { xs: 8, lg: 10 },
              alignItems: "center",
            }}
          >
            {/* Left copy */}
            <Box
              className="hero-content"
              sx={{
                maxWidth: 640,
                order: { xs: 2, lg: 1 },
              }}
            >
              <Chip
                label={t("hero.badge")}
                sx={{
                  bgcolor: "#F5F2FF",
                  color: PURPLE,
                  border: "1px solid #E7E0FF",
                  fontWeight: 600,
                  mb: 3,
                }}
              />
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "40px", lg: "56px" },
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                  mb: 3,
                }}
              >
                {t("hero.titleLine1")}
                <br />
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(90deg, ${PURPLE}, #2563eb)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {t("hero.titleLine2")}
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontSize: "18px",
                  color: "#475569",
                  lineHeight: 1.7,
                  mb: 4,
                }}
              >
                {t("hero.description")}
              </Typography>
              <Box
                className="hero-technologies"
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                  opacity: 0.6,
                  filter: "grayscale(1)",
                }}
              >
                <Typography sx={{ fontSize: "20px", color: PURPLE }}>
                  •
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>
                  ANDROID
                </Typography>

                <Typography sx={{ fontSize: "20px", color: PURPLE }}>
                  •
                </Typography>

                <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>
                  AWS
                </Typography>

                <Typography sx={{ fontSize: "20px", color: PURPLE }}>
                  •
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "20px",
                    letterSpacing: "-0.02em",
                  }}
                >
                  NAYAX
                </Typography>
              </Box>
            </Box>

            {/* Right: single-step login card */}
            <Box
              sx={{
                width: "100%",
                maxWidth: 440,
                mx: { xs: "auto", lg: 0 },
                ml: { lg: "auto" },
                order: { xs: 1, lg: 2 },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  bgcolor: "#fff",
                  borderRadius: "24px",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
                  border: "1px solid #2c2c2c",
                  p: { xs: 4, sm: 5 },
                  textAlign: "center",
                  overflow: "hidden",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#64748b", mb: 4 }}>
                  {t("login.subtitle")}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                    textAlign: "left",
                  }}
                >
                  {/* Email field (single step) */}
                  <TextField
                    name="identifier"
                    placeholder={t("login.emailPlaceholder")}
                    fullWidth
                    value={formData.identifier}
                    onChange={handleInputChange}
                    error={!!errors.identifier}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    sx={textFieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <EmailOutlinedIcon
                            sx={{ color: "#94a3b8", fontSize: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors.identifier && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ mt: "-12px" }}
                    >
                      {errors.identifier}
                    </Typography>
                  )}

                  {/* Password field (same step) */}
                  <TextField
                    name="password"
                    placeholder={t("login.passwordPlaceholder")}
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    error={!!errors.password}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    sx={textFieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          onClick={() => setShowPassword((s) => !s)}
                          sx={{ cursor: "pointer" }}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon sx={{ color: "#94a3b8" }} />
                          ) : (
                            <VisibilityIcon sx={{ color: "#94a3b8" }} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors.password && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ mt: "-12px" }}
                    >
                      {errors.password}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: "-12px",
                    }}
                  >
                    <Link
                      component="button"
                      type="button"
                      onClick={() => {
                        setForgotEmail("");
                        setForgotDialogOpen(true);
                      }}
                      sx={{
                        color: PURPLE,
                        fontSize: "13px",
                        textDecoration: "underline",
                      }}
                    >
                      {t("login.forgotPassword")}
                    </Link>
                  </Box>

                  <FormControlLabel
                    sx={{ mt: "-8px" }}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          setChecked(e.target.checked);
                          if (errors.checkbox)
                            setErrors((prev) => ({ ...prev, checkbox: "" }));
                        }}
                        sx={{
                          color: "#cbd5e1",
                          "&.Mui-checked": { color: PURPLE },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "13px", color: "#64748b" }}>
                        {t("login.agreeText")}{" "}
                        <Link
                          href="/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: PURPLE, textDecorationColor: PURPLE }}
                        >
                          {t("franchise.TermsConditions")}
                        </Link>{" "}
                        {t("login.and")}{" "}
                        <Link
                          href="https://vendinaf.com/de/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: PURPLE, textDecorationColor: PURPLE }}
                        >
                          {t("footer.privacyPolicy")}
                        </Link>
                      </Typography>
                    }
                  />
                  {errors.checkbox && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ mt: "-12px" }}
                    >
                      {errors.checkbox}
                    </Typography>
                  )}
                  {errors.form && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ textAlign: "center" }}
                    >
                      {errors.form}
                    </Typography>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    onClick={handleLogin}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "14px",
                      py: 1.6,
                      borderRadius: "12px",
                      bgcolor: PURPLE,
                      boxShadow: "0 4px 14px rgba(91,69,255,0.25)",
                      "&:hover": { bgcolor: PURPLE_DARK },
                    }}
                  >
                    {loading ? t("login.loggingIn") : t("login.loginButton")}
                  </Button>
                </Box>

                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#94a3b8",
                    mt: 4,
                    pt: 3,
                    borderTop: "1px solid #f1f1f4",
                  }}
                >
                  {t("login.footerNote")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Features */}
        <Box
          id="features"
          sx={{ py: 12, bgcolor: "#fff", borderTop: "1px solid #f1f1f4" }}
        >
          <Box
            sx={{ maxWidth: "1280px", mx: "auto", px: { xs: 2, sm: 3, lg: 4 } }}
          >
            <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto", mb: 8 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "30px", md: "36px" },
                  mb: 2,
                }}
              >
                {t("features.title")}
              </Typography>
              <Typography sx={{ fontSize: "18px", color: "#475569" }}>
                {t("features.subtitle")}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                  lg: "1fr 1fr 1fr",
                },
                gap: 4,
              }}
            >
              <FeatureCard
                icon={<BarChartIcon />}
                title={t("features.salesAnalytics.title")}
                desc={t("features.salesAnalytics.desc")}
              />
              <FeatureCard
                icon={<Inventory2OutlinedIcon />}
                title={t("features.inventory.title")}
                desc={t("features.inventory.desc")}
              />
              <FeatureCard
                icon={<AdsClickIcon />}
                title={t("features.advertising.title")}
                desc={t("features.advertising.desc")}
              />
              <FeatureCard
                icon={<MonitorOutlinedIcon />}
                title={t("features.monitoring.title")}
                desc={t("features.monitoring.desc")}
              />
              <FeatureCard
                icon={<GroupsOutlinedIcon />}
                title={t("features.crm.title")}
                desc={t("features.crm.desc")}
              />
              <FeatureCard
                icon={<DescriptionOutlinedIcon />}
                title={t("features.taxReporting.title")}
                desc={t("features.taxReporting.desc")}
              />
              <Box sx={{ gridColumn: { md: "1 / -1" } }}>
                <Box
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: "16px",
                    p: 4,
                    border: "1px solid #f1f1f4",
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: "center",
                    gap: 3,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      bgcolor: "#EFF6FF",
                      color: "#2563eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ShieldOutlinedIcon sx={{ fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: "20px", mb: 1 }}
                    >
                      {t("features.security.title")}
                    </Typography>
                    <Typography sx={{ color: "#475569" }}>
                      {t("features.security.desc")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Specifications */}
        <Box id="specifications" sx={{ py: 12, bgcolor: "#fff" }}>
          <Box
            sx={{ maxWidth: "1000px", mx: "auto", px: { xs: 2, sm: 3, lg: 4 } }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "30px", md: "36px" },
                textAlign: "center",
                mb: 8,
              }}
            >
              {t("specifications.title")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: 8,
                rowGap: 8,
              }}
            >
              <SpecBlock
                title={t("specifications.salesPerformance.title")}
                items={specSalesItems}
              />
              <SpecBlock
                title={t("specifications.machines.title")}
                items={specMachineItems}
              />
              <SpecBlock
                title={t("specifications.products.title")}
                items={specProductItems}
              />
              <SpecBlock
                title={t("specifications.userSecurity.title")}
                items={specUserSecurityItems}
              />
            </Box>
          </Box>
        </Box>

        {/* "Flexible Plans" section  */}
        {/* <Box
          id="pricing"
          sx={{ bgcolor: "#fff", py: 4, scrollMarginTop: "100px" }}
        >
          <Box
            sx={{ maxWidth: "1280px", mx: "auto", px: { xs: 2, sm: 3, lg: 4 } }}
            className="pricing-section"
          >
            <Typography
              variant="h3"
              component="h2"
              className="pricing-section__heading"
              sx={{ fontWeight: 700, mt: 4, mb: 5, textAlign: "center" }}
            >
              {t("pricing.title")}
            </Typography>
            <Typography
              className="pricing-section__subtitle"
              sx={{
                textAlign: "center",
                mx: "auto",
                mt: 4,
                mb: 5,
                lineHeight: 1.7,
              }}
            >
              {t("pricing.subtitle")}
            </Typography>

            <div className="pricing-grid">
              <DarkPricingCard
                name={t("pricing.basic.name")}
                price="€0"
                period={t("pricing.free")}
                desc={t("pricing.basic.desc")}
                features={basicFeatures}
                getStartedLabel={t("pricing.getStarted")}
              />
              <DarkPricingCard
                name={t("pricing.premium.name")}
                price="€49"
                period={t("pricing.perMonth")}
                desc={t("pricing.premium.desc")}
                features={premiumFeatures}
                getStartedLabel={t("pricing.getStarted")}
              />
              <DarkPricingCard
                name={t("pricing.enterprise.name")}
                price="€99"
                period={t("pricing.perMonth")}
                desc={t("pricing.enterprise.desc")}
                features={enterpriseFeatures}
                getStartedLabel={t("pricing.getStarted")}
              />
            </div>
          </Box>
        </Box> */}

        {/* Footer */}
        <Box sx={{ bgcolor: "#fff", borderTop: "1px solid #e5e7eb", py: 6 }}>
          <Box
            sx={{
              maxWidth: "1280px",
              mx: "auto",
              px: { xs: 2, sm: 3, lg: 4 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                component="img"
                src={logo}
                alt="Logo"
                sx={{ height: 28, width: "auto" }}
              />
              <Typography sx={{ fontWeight: 700 }}>
                {t("footer.brand")}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#64748b",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              {t("footer.copyright", { year: new Date().getFullYear() })}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Link
                href="https://vendinaf.com/de/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontSize: "14px", color: "#64748b" }}
              >
                {t("footer.privacyPolicy")}
              </Link>

              <Box component="span" sx={{ color: "#2c2c2c", fontSize: "14px" }}>
                |
              </Box>

              <Link
                href="https://naf-cloudsystem.de/privacy"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontSize: "14px", color: "#64748b" }}
              >
                {t("footer.terms")}
              </Link>
            </Box>
          </Box>
        </Box>

        {/* Forgot password dialog box */}

        <Dialog
          open={forgotDialogOpen}
          onClose={() => setForgotDialogOpen(false)}
          maxWidth="540px"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "18px",
              p: 1,
              maxWidth: "550px",
              bgcolor: "#FFF",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
              pt: 3,
              px: 3,
            }}
          >
            <Typography
              sx={{
                color: "#111827",
                fontSize: "25px",
                fontWeight: "500px",
                fontFamily: "Inter",
              }}
            >
              {t("login.forgotPasswordDialog.title")}
            </Typography>

            <IconButton
              onClick={() => {
                setForgotDialogOpen(false);
                setForgotEmail("");
                setForgotEmailError("");
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 3 }}>
            <Typography
              sx={{
                color: "#5B6270",
                fontSize: 14,
                lineHeight: 1.6,
                mb: 3,
                fontFamily: "Satoshi-medium",
                width: "370px",
              }}
            >
              {t("login.forgotPasswordDialog.description")}
            </Typography>

            <Typography
              sx={{
                color: "#4A4A4A",
                fontWeight: 500,
                fontSize: "13px",
                mb: 1,
                fontfamily: "Overused Grotesk",
              }}
            >
              {t("login.forgotPasswordDialog.emailLabel")}
            </Typography>

            <TextField
              fullWidth
              autoFocus
              placeholder={t("login.forgotPasswordDialog.emailPlaceholder")}
              value={forgotEmail}
              onChange={(e) => {
                setForgotEmail(e.target.value);

                if (forgotEmailError) {
                  setForgotEmailError("");
                }
              }}
              error={!!forgotEmailError}
              helperText={forgotEmailError}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#EFF3F6",

                  "& fieldset": {
                    border: "none",
                  },

                  "&:hover fieldset": {
                    border: "none",
                  },

                  "&.Mui-focused fieldset": {
                    border: "1px solid #5B45FF",
                  },
                  "&::placeholder": {
                    color: "#0A1E06",
                    opacity: 2,
                    fontFamily: "sathoshi",
                  },
                },
              }}
            />

            <Box
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: "1px solid #F3CF6B",
                backgroundColor: "#FBF2D0",
                borderRadius: "25px",
                px: 2,
                py: 1,
              }}
            >
              <WarningAmberRoundedIcon
                sx={{
                  color: "#BD9400",
                  fontSize: 18,
                }}
              />

              <Typography
                sx={{
                  fontSize: 12,
                  color: "#3F4654",
                  lineHeight: 1.4,
                }}
              >
                {t("login.forgotPasswordDialog.note")}
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              justifyContent: "flex-end",
              gap: 1,
              px: 3,
              pb: 3,
              pt: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setForgotDialogOpen(false);
                setForgotEmail("");
                setForgotEmailError("");
              }}
              sx={{
                borderRadius: "30px",
                textTransform: "none",
                padding: "10px 32px",

                color: "#0A1E06",
                borderColor: "#161616",
              }}
            >
              {t("login.forgotPasswordDialog.cancel")}
            </Button>

            <Button
              variant="contained"
              sx={{
                bgcolor: "#4C33DB",
                borderRadius: "30px",
                textTransform: "none",
                padding: "10px 32px",
                //minWidth: 220,
                fontFamily: "satoshi",
                color: "#fff",

                "&:hover": {
                  bgcolor: PURPLE_DARK,
                },
              }}
              onClick={async () => {
                if (!forgotEmail.trim()) {
                  setForgotEmailError(
                    t("login.forgotPasswordDialog.errors.emailRequired"),
                  );
                  return;
                }

                if (!isValidEmail(forgotEmail)) {
                  setForgotEmailError(
                    t("login.forgotPasswordDialog.errors.invalidEmail"),
                  );
                  return;
                }

                setForgotEmailError("");

                try {
                  await forgotPassword(forgotEmail);

                  setForgotDialogOpen(false);
                  setForgotEmail("");

                  setSnackbarMessage(t("login.snackbar.resetSuccess"));
                  setSnackbarSeverity("success");
                  setSnackbarOpen(true);
                } catch (error) {
                  // Keep field errors below the input; snackbar stays success-only
                  setForgotEmailError(
                    t("login.forgotPasswordDialog.errors.invalidEmail"),
                  );
                }
              }}
            >
              {t("login.forgotPasswordDialog.sendButton")}
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default NAFLandingLogin;
